import { DocumentServicesObject } from '@wix/document-services-types';
import { EditorElement } from '@wix/component-protocol/schema';
import { ManifestComponentExtensionType } from '../stores';
import { AbstractManifestMiddleware } from './AbstractManifestMiddleware';
import {
  ChangeType,
  ManifestChangeDetection,
} from '../ManifestChangeDetection';
import { ManifestServiceFlags } from '../ManifestServiceFlags';
import { ManifestServiceStats } from '../ManifestServiceStats';
import {
  VirtualElement,
  VirtualElementAPIType,
} from '../api/providers/VirtualElement';
import { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

enum OverrideCacheStatus {
  Resolved = 'resolved',
  Invalidated = 'invalidated',
}

export class DynamicOverridesMiddleware extends AbstractManifestMiddleware {
  isExternalMiddleware = true as const;

  #cache = new Map<
    string,
    {
      status: OverrideCacheStatus;
      node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>;
      value: EditorElement | null;
    }
  >();

  constructor(
    editorPointerAPI: EditorPointerServiceAPI,
    private readonly dm: DocumentServicesObject,
    private readonly manifestChangeDetection: ManifestChangeDetection,
    private readonly manifestServiceFlags: ManifestServiceFlags,
    private readonly manifestServiceStats: ManifestServiceStats,
  ) {
    super(editorPointerAPI);

    this.manifestChangeDetection.subscribe((patch) => {
      const unexistingKeys: string[] = [];

      this.#cache.forEach((_, key) => {
        const actualValue = this.#cache.get(key);
        const editorPointer = actualValue?.node.getEditorPointer();

        const isExists = editorPointer
          ? this.dm.components.is.exist(
              this.editorPointerAPI.getDocumentPointer(editorPointer),
            )
          : false;

        if (!isExists) {
          unexistingKeys.push(key);
        }
      });

      unexistingKeys.forEach((key) => {
        this.#cache.delete(key);
      });

      const invalidatedKeys: string[] = [];

      // for component patches – invalidate overrides per component
      if (ManifestChangeDetection.isComponentPatch(patch)) {
        if (
          patch.type === ChangeType.Styles ||
          patch.type === ChangeType.Data ||
          patch.type === ChangeType.Preset ||
          patch.type === ChangeType.Invalidate ||
          patch.type === ChangeType.State ||
          patch.type === ChangeType.DesignState
        ) {
          /**
           * TODO: should we use
           * this.manifestChangeDetection.isPatchMatchEditorPointers
           *
           * NOTE: the inner element path must stay in forward order. The
           * prefixes below are turned into cache keys, and a cached key for a
           * nested element is `<doc>--a,b,c` (forward). Reversing the path
           * produced keys like `<doc>--c,b,a` that never matched, so a deeply
           * nested element's override was never invalidated on change.
           */
          const innerElementPath = [
            ...(this.editorPointerAPI.getInnerElementPath(
              patch.editorPointer,
            ) ?? []),
          ];

          const patchDocumentPointer = this.editorPointerAPI.getDocumentPointer(
            patch.editorPointer,
          );

          const editorPointers = [
            ...innerElementPath.map((_x, index) => {
              return this.editorPointerAPI.buildEditorPointer(
                patchDocumentPointer,
                innerElementPath.slice(0, index + 1),
              );
            }),
            this.editorPointerAPI.buildEditorPointer(patchDocumentPointer),
          ];

          editorPointers.forEach((editorPointer) => {
            if (this.#cache.has(editorPointer.asUniqueKey)) {
              invalidatedKeys.push(editorPointer.asUniqueKey);
            }
          });
        }
      } else {
        // for global patches – invalidate all overrides
        invalidatedKeys.push(...this.#cache.keys());
      }

      if (!invalidatedKeys.length) {
        return;
      }

      invalidatedKeys.forEach((key) => {
        this.manifestServiceStats.cache(
          'DynamicBehaviour',
          key,
          'invalidated',
          this.#cache.get(key)!,
          `${patch.type} change`,
        );

        this.#cache.set(key, {
          ...this.#cache.get(key)!,
          status: OverrideCacheStatus.Invalidated,
        });
      });

      /**
       * recalculate invalidated caches
       * probably this may cause performance issues
       * when invalidate a lot of registered components,
       * especially in the case of Global-* a patch
       *
       * as the solution, we can integrate invalidated caches in async way
       * – per animation frame, since we keep status,
       * the cache might be invalidated right here or between the loop on request
       */
      const scheduleFunction =
        typeof window !== 'undefined'
          ? window.requestAnimationFrame
          : globalThis.setTimeout;

      this.#cache.forEach(({ status }, key) => {
        if (status === OverrideCacheStatus.Invalidated) {
          scheduleFunction(() => {
            const actualValue = this.#cache.get(key);
            if (!actualValue) {
              return;
            }

            /**
             * before recalculation, check if it was already recalculated by request
             * in between scheduler calls
             */
            if (actualValue.status === OverrideCacheStatus.Invalidated) {
              this.#resolveOverrides(actualValue.node);
            }
          });
        }
      });
    });
  }

  #resolveOverrides(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
  ) {
    const ms = performance.now();

    const editorPointer = node.getEditorPointer();
    const extensionType = node.getExtensionType();

    const resolvedOverrides = this.#cache.get(editorPointer.asUniqueKey);

    if (
      this.manifestServiceFlags.isManifestDynamicOverridesCacheEnabled &&
      resolvedOverrides?.status === OverrideCacheStatus.Resolved
    ) {
      this.manifestServiceStats.method(
        'resolveDynamicOverrides',
        true,
        performance.now() - ms,
      );

      if (!resolvedOverrides.value) {
        return null;
      }

      return resolvedOverrides.value!;
    }

    let overrides: EditorElement | null = null;

    switch (extensionType) {
      case ManifestComponentExtensionType.BUILDER: {
        overrides = this.#resolveBuilderComponentManifest(node);
        break;
      }

      case ManifestComponentExtensionType.OOI: {
        overrides = this.#resolveOOIComponentManifest(node);
        break;
      }
    }

    this.#triggerManifestDefinitionChange(node, overrides);

    const value = {
      status: OverrideCacheStatus.Resolved,
      value: overrides,
      node,
    };

    this.manifestServiceStats.cache(
      'DynamicBehaviour',
      editorPointer.asUniqueKey,
      'created',
      { status: value.status, value: value.value },
    );

    this.#cache.set(editorPointer.asUniqueKey, value);

    this.manifestServiceStats.method(
      'resolveDynamicOverrides',
      false,
      performance.now() - ms,
    );

    if (!overrides) {
      return null;
    }

    return overrides;
  }

  #getResolverAPI(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
    options: {
      supportInnerElementPath?: boolean;
    },
  ) {
    const supportInnerElementPath = !!options?.supportInnerElementPath;

    const getVirtualElement = (innerElementPath?: string[]) => {
      return supportInnerElementPath && innerElementPath
        ? node.getChild(innerElementPath)
        : node;
    };

    return {
      getCurrentProps: (innerElementPath?: string[]) => {
        const api = getVirtualElement(innerElementPath)?.getAPI();
        return api ? api.query.getData() : {};
      },
      getCurrentStyles: (innerElementPath?: string[]) => {
        const api = getVirtualElement(innerElementPath)?.getAPI();

        return api
          ? api.query.getStyles()
          : {
              cssProperties: {},
              cssCustomProperties: {},
            };
      },
      getCurrentPreset: (innerElementPath?: string[]) => {
        const api = getVirtualElement(innerElementPath)?.getAPI();

        return api ? api.query.getAppliedPreset() : null;
      },
      getCurrentState: () => {
        return getVirtualElement()?.getAPI().query.getState() ?? null;
      },
    };
  }

  #resolveOOIComponentManifest(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
  ): EditorElement | null {
    const metadata = node.getManifestMetadata();

    if (typeof metadata?.resolveManifestOverrides === 'function') {
      const overrides = metadata.resolveManifestOverrides({
        ...this.#getResolverAPI(node, {
          supportInnerElementPath: false,
        }),
      });

      return overrides ? overrides : null;
    }

    return null;
  }

  #resolveBuilderComponentManifest(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
  ): EditorElement | null {
    if (!this.dm.components.runtime.resolveManifestOverrides) {
      return null;
    }

    const componentType = node.getComponentType();

    if (!componentType) {
      return null;
    }

    const overrides = this.dm.components.runtime.resolveManifestOverrides(
      componentType,
      {
        ...this.#getResolverAPI(node, {
          supportInnerElementPath: true,
        }),
      },
    );

    return overrides ? overrides : null;
  }

  #triggerManifestDefinitionChange(
    node: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>,
    overrides: null | EditorElement, // include overrides only
  ) {
    const notify = () => {
      /**
       * TODO: we need to check which inner elements were overridden
       * then notify Manifest change for inner elements
       */
      this.manifestChangeDetection.notify({
        type: ChangeType.Manifest,
        editorPointer: node.getEditorPointer(),
        extensionType: node.getExtensionType(),
        value: overrides,
      });
    };

    const currentOverrides =
      this.#cache.get(node.getEditorPointer().asUniqueKey) ?? null;

    const a = JSON.stringify(currentOverrides?.value ?? null);
    const b = JSON.stringify(overrides ?? null);

    if (a !== b) {
      notify();
    }
  }

  resolveRootElement: AbstractManifestMiddleware['resolveRootElement'] = (
    node,
  ) => {
    const overrides = this.#resolveOverrides(node);

    if (!overrides) {
      return null;
    }

    return {
      editorElement: overrides,
    };
  };

  resolveChildRefElement: AbstractManifestMiddleware['resolveChildRefElement'] =
    (node) => {
      return this.#resolveOverrides(node) ?? null;
    };
}
